<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";

import { sysConfig } from "~shared/config/system.cfg.js";
import { useAuth } from "@/composables/useAuth.js";
import { useIdleLogout } from "@/composables/useIdleLogout";
import { preloadVariantData } from "@/utils/preloadVariants.js";

import Modal from "@/components/modal/Modal.vue";
import ModalManager from "@/components/modal/ModalManager.js";

import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import MainNav from "@/components/MainNav.vue";
import SubNav from "@/components/SubNav.vue";

import { useModelStore } from "@/composables/useModelStore.js";
const { models, currentModel, setModel } = useModelStore();

/* ============================================================
   Auth + idle
============================================================ */
const { user, logout } = useAuth();
const isLoggedIn = computed(() => !!user.value);

let idleInstance = null;

window.__idleProgress = (pct) => {
  if (idleInstance) idleInstance.progress.value = pct;
};

const progress = computed(() => idleInstance?.progress.value ?? 0);

/* ============================================================
   Lifecycle
============================================================ */
onMounted(async () => {
  if (localStorage.getItem("api_key")) {
    await preloadVariantData();
  }
});

watch(
  isLoggedIn,
  (logged) => {
    idleInstance = logged
      ? useIdleLogout({
          timeoutMs: sysConfig.idleDelayMs,
          heartbeatMs: 1000,
          onLogout: logout
        })
      : null;
  },
  { immediate: true }
);

/* ============================================================
   Navigation state
============================================================ */
const mainNavOpen = ref(false);
const activeMain = ref("PIM");

const router = useRouter();
const route = useRoute();

/* ============================================================
   SubNav memory helpers
============================================================ */
const STORAGE_KEY = "nz:lastSub";

function readLastSub() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function writeLastSub(system, value) {
  const state = readLastSub();
  state[system] = value;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

/* ============================================================
   Derive active main system from route
============================================================ */
function deriveMainFromPath(path) {
  if (path.startsWith("/cp")) return "CP";
  if (path.startsWith("/sap")) return "SAP";
  if (path.startsWith("/bmd")) return "BMD";
  if (path.startsWith("/base")) return "BASE";
  return "PIM";
}

watch(
  () => route.path,
  (path) => {
    activeMain.value = deriveMainFromPath(path);
    mainNavOpen.value = false;

    // Remember last sub-route for routed systems
    if (path.startsWith("/cp")) writeLastSub("CP", path);
    else if (path.startsWith("/sap")) writeLastSub("SAP", path);    
    else if (path.startsWith("/bmd")) writeLastSub("BMD", path);
    else if (path.startsWith("/base")) writeLastSub("BASE", path);
  },
  { immediate: true }
);

/* ============================================================
   UI actions
============================================================ */
function toggleMainNav() {
  mainNavOpen.value = !mainNavOpen.value;
}

function setMainState(system) {
  mainNavOpen.value = false;

  const last = readLastSub()[system];

  if (system === "PIM") {
    if (last) setModel(last);
    router.push("/dashboard");
    return;
  }

  if (last) {
    router.push(last);
    return;
  }

  // fallback defaults
  switch (system) {
    case "CP":
      router.push("/cp/sync");
      break;
    case "SAP":
      router.push("/sap/sync");
      break;      
    case "BMD":
      router.push("/bmd/config");
      break;
    case "BASE":
      router.push("/base/config");
      break;
  }
}
</script>


<template>
  <div class="app-layout">
    <Header
      :progress="progress"
      :onLogout="logout"
      :active="mainNavOpen"
      :activeMain="activeMain"
      @toggle-nav="toggleMainNav"
    />

    <MainNav
      :open="mainNavOpen"
      :active="activeMain"
      @select="setMainState"
      @close="mainNavOpen = false"
    />

    <SubNav
      :state="activeMain"
      :models="models"
      :currentModel="currentModel"
      @selectModel="setModel"
    />

    <main class="app-content">
      <router-view />
    </main>

    <Footer />
  </div>

  <Modal
    v-for="(modal, index) in ModalManager.state.stack"
    :key="index"
    :title="modal.title"
    :record="modal.record"
    :size="modal.size"
    :detectValidation="modal.detectValidation"
    :style="{ zIndex: 1000 + index * 10 }"
    @close="payload => ModalManager.close(payload)"
  >
  <component
    :is="modal.component"
    v-bind="modal.props"
    @close="payload => ModalManager.close(payload)"
  />
  </Modal>

</template>
